import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  GetUserRequest,
  User,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { getUser } from "../../../../api/endpoints/user";
import { gRPCStatus } from "../../../../api/grpc-status-codes";
import { callOptionsHeaders } from "../../../../api/helpers";
import { DocPrefix } from "../../../../constants";
import {
  GrpcError,
  SnackbarContext,
} from "../../../../context/snackbar-context";
import { toS12ID } from "../../../../helpers/s12-helpers";

type UserTypeProps = {
  regionCode: string;
  roleId: string;
  userIds: string[];
};

export const UserType = (props: UserTypeProps) => {
  const { userIds, roleId, regionCode } = props;
  const { showError } = useContext(SnackbarContext);
  const [users, setUsers] = useState<User[] | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const loadUser = useCallback(
    async (userId: string) => {
      const req = new GetUserRequest({
        userId: toS12ID(DocPrefix.PrefixUser, userId),
      });
      return await getUser(req, callOptionsHeaders(regionCode, roleId));
    },
    [regionCode, roleId],
  );

  const loadUsers = useCallback(async () => {
    try {
      const userResults = await Promise.all(
        userIds.map((userId) =>
          loadUser(userId).catch(() => {
            return undefined;
          }),
        ),
      );

      const validUsers = userResults.filter(
        (user): user is User => user !== undefined,
      );

      if (validUsers.length === 0 || validUsers.length < userIds.length) {
        showError({
          name: "Some users not found",
          code: gRPCStatus.NOT_FOUND,
          message: "Some users not found",
        });
      }

      setUsers(validUsers);
    } catch (error) {
      showError(error as GrpcError);
    } finally {
      setIsLoading(false);
    }
  }, [loadUser, userIds, showError]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!users) {
    return <div>Failed to load users</div>;
  }

  if (users.length > 1) {
    return (
      <ul style={{ textAlign: "left", paddingLeft: 0 }}>
        {users.map((value) => (
          <li data-anchor="user-attribute-user" key={value.email}>
            {value.firstName} {value.lastName} ({value.email})
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div data-anchor="user-attribute-string">
      {users[0].firstName} {users[0].lastName} ({users[0].email})
    </div>
  );
};
